import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Save, Plus, Trash2, Image, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/vd09zrou_Logo%20-%20Final.png";

const colorOptions = [
  { value: 'pink', label: 'Pink', class: 'bg-sip-pink' },
  { value: 'blue', label: 'Blue', class: 'bg-sip-blue' },
  { value: 'yellow', label: 'Yellow', class: 'bg-sip-yellow' },
  { value: 'navy', label: 'Navy', class: 'bg-sip-navy' }
];

export const AdminHomepage = () => {
  const [drinkImages, setDrinkImages] = useState([
    { id: "1", url: "", label: "Yum!", color: "pink" },
    { id: "2", url: "", label: "Sweet!", color: "blue" },
    { id: "3", url: "", label: "Fun!", color: "yellow" },
    { id: "4", url: "", label: "Tasty!", color: "navy" }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/homepage-settings`);
      if (response.ok) {
        const data = await response.json();
        if (data.drink_images && data.drink_images.length > 0) {
          setDrinkImages(data.drink_images);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleImageChange = (index, url) => {
    const updated = [...drinkImages];
    updated[index].url = url;
    setDrinkImages(updated);
  };

  const handleLabelChange = (index, label) => {
    const updated = [...drinkImages];
    updated[index].label = label;
    setDrinkImages(updated);
  };

  const handleColorChange = (index, color) => {
    const updated = [...drinkImages];
    updated[index].color = color;
    setDrinkImages(updated);
  };

  const addDrinkImage = () => {
    if (drinkImages.length >= 6) {
      toast.error('Maximum 6 drink images allowed');
      return;
    }
    setDrinkImages([
      ...drinkImages,
      { id: Date.now().toString(), url: "", label: "Wow!", color: "pink" }
    ]);
  };

  const removeDrinkImage = (index) => {
    if (drinkImages.length <= 2) {
      toast.error('Minimum 2 drink images required');
      return;
    }
    const updated = drinkImages.filter((_, i) => i !== index);
    setDrinkImages(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/homepage-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ drink_images: drinkImages })
      });

      if (response.ok) {
        toast.success('Homepage settings saved!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-homepage">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-sip-purple transition-colors">
                <ArrowLeft size={24} />
              </Link>
              <Link to="/">
                <img src={LOGO_URL} alt="Sip Fantasia" className="h-10 w-auto" />
              </Link>
            </div>
            <h1 className="font-script text-xl md:text-2xl text-gray-900">Homepage Settings</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <Home className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Customize Your Homepage</h3>
              <p className="text-blue-700 text-sm mt-1">
                Add or update the drink images that appear in the hero collage on your homepage. Each image can have a custom label and border color.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-sip-purple border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading settings...</p>
          </div>
        ) : (
          <>
            {/* Drink Images */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Drink Images</h2>
                <button
                  onClick={addDrinkImage}
                  className="flex items-center gap-2 px-4 py-2 bg-sip-blue text-white rounded-full hover:bg-sip-blue/90 transition-colors"
                >
                  <Plus size={18} />
                  Add Image
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {drinkImages.map((image, index) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-2 border-gray-200 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium text-gray-700">Image {index + 1}</span>
                      <button
                        onClick={() => removeDrinkImage(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove image"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    {/* Image Preview/Upload */}
                    <div className="mb-4">
                      {image.url ? (
                        <div className="relative">
                          <img
                            src={image.url}
                            alt={`Drink ${index + 1}`}
                            className="w-full h-48 object-contain rounded-lg bg-gray-100"
                          />
                          <button
                            onClick={() => handleImageChange(index, '')}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : (
                        <ImageUpload
                          onUpload={(url) => handleImageChange(index, url)}
                          currentImage=""
                        />
                      )}
                    </div>

                    {/* Or paste URL */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Or paste image URL</label>
                      <Input
                        type="url"
                        value={image.url}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        placeholder="https://..."
                        className="text-sm"
                      />
                    </div>

                    {/* Label */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Badge Label</label>
                      <Input
                        type="text"
                        value={image.label}
                        onChange={(e) => handleLabelChange(index, e.target.value)}
                        placeholder="e.g., Yum!"
                        maxLength={10}
                      />
                    </div>

                    {/* Color */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Border Color</label>
                      <div className="flex gap-2">
                        {colorOptions.map((color) => (
                          <button
                            key={color.value}
                            onClick={() => handleColorChange(index, color.value)}
                            className={`w-10 h-10 rounded-full ${color.class} ${
                              image.color === color.value ? 'ring-2 ring-offset-2 ring-gray-800' : ''
                            } transition-all hover:scale-110`}
                            title={color.label}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Preview</h2>
              <div className="bg-gradient-to-br from-sip-blue/20 via-sip-pink/10 to-sip-yellow/20 rounded-xl p-6">
                <div className="flex flex-wrap items-center justify-center gap-4">
                  {drinkImages.filter(img => img.url).map((image, index) => (
                    <div
                      key={image.id}
                      className={`bg-white p-2 rounded-xl shadow-lg border-4 border-sip-${image.color} transform ${
                        index % 2 === 0 ? '-rotate-2' : 'rotate-2'
                      }`}
                    >
                      <img src={image.url} alt="Preview" className="w-24 h-auto object-contain rounded-lg" />
                      <div className={`absolute -bottom-1 -right-1 bg-sip-${image.color} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                        {image.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-sip-navy text-white rounded-full hover:bg-sip-navy/90 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminHomepage;
